import { calculateDistance } from './distance';
import { decodePolyline } from './polyline';
import { pausedMsBetween, remainingPauseMs } from './tripPauses';

const MIN_SPEED_KMH = 20;
const MAX_SPEED_KMH = 110;
// The observed speed is only trusted once this share of the route has been covered.
const OBSERVED_SPEED_MIN_SHARE = 0.15;
// Straight-line distances underestimate road distances; used only without a stored route.
const ROAD_FACTOR = 1.25;

const KM_PER_DEG_LAT = 110.57;
const KM_PER_DEG_LON_AT_EQUATOR = 111.32;

/**
 * Distance left on the route from the point of the route nearest to `position`, plus the
 * distance between the position and that point (so a detour counts).
 */
const remainingAlongRoute = (route, position) => {
  const cosLat = Math.cos((position.latitude * Math.PI) / 180);
  const toXY = (p) => [
    (p.longitude - position.longitude) * KM_PER_DEG_LON_AT_EQUATOR * cosLat,
    (p.latitude - position.latitude) * KM_PER_DEG_LAT,
  ];

  const cumulative = [0];
  for (let i = 1; i < route.length; i += 1) {
    cumulative.push(cumulative[i - 1] + calculateDistance(
      route[i - 1].latitude,
      route[i - 1].longitude,
      route[i].latitude,
      route[i].longitude,
    ));
  }
  const total = cumulative[cumulative.length - 1];

  let best = { offRoute: Infinity, along: 0 };
  for (let i = 0; i < route.length - 1; i += 1) {
    const [x1, y1] = toXY(route[i]);
    const [x2, y2] = toXY(route[i + 1]);
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lengthSq = dx * dx + dy * dy;
    // The position is the origin: project it on the segment.
    const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, -(x1 * dx + y1 * dy) / lengthSq));
    const offRoute = Math.hypot(x1 + t * dx, y1 + t * dy);
    if (offRoute < best.offRoute) {
      best = { offRoute, along: cumulative[i] + t * (cumulative[i + 1] - cumulative[i]) };
    }
  }
  return { remainingKm: total - best.along + best.offRoute, doneKm: best.along, totalKm: total };
};

/**
 * Live progress of a trip: remaining distance, estimated arrival and gap to the planned arrival.
 * Speed is the planned average until enough of the route is done, then the observed one
 * (over moving time). Declared pauses still running are added to the estimated arrival.
 */
const computeTripProgress = ({
  routeGeometry, lastLocation, arrivalCoords, startedAt, distanceInKm, estimatedDuration,
  plannedArrival, pauses,
}) => {
  if (!lastLocation) return null;

  let remainingKm;
  let doneKm = null;
  let totalKm = distanceInKm ?? null;
  let approximate = false;

  const route = routeGeometry ? decodePolyline(routeGeometry) : [];
  if (route.length > 1) {
    ({ remainingKm, doneKm, totalKm } = remainingAlongRoute(route, lastLocation));
  } else if (arrivalCoords) {
    remainingKm = ROAD_FACTOR * calculateDistance(
      lastLocation.latitude,
      lastLocation.longitude,
      arrivalCoords.lat,
      arrivalCoords.lon,
    );
    approximate = true;
  } else {
    return null;
  }

  const lastTimestamp = new Date(lastLocation.timestamp).getTime();
  const plannedSpeed = distanceInKm && estimatedDuration
    ? distanceInKm / (estimatedDuration / 60) : null;

  let speed = plannedSpeed;
  if (startedAt && doneKm !== null && totalKm > 0 && doneKm / totalKm >= OBSERVED_SPEED_MIN_SHARE) {
    // Moving time only: stops (rest, fuel) must not make the driver look slow.
    const startMs = new Date(startedAt).getTime();
    const movingMs = lastTimestamp - startMs - pausedMsBetween(pauses, startMs, lastTimestamp);
    if (movingMs > 0) speed = doneKm / (movingMs / 3600000);
  }
  if (speed) speed = Math.max(MIN_SPEED_KMH, Math.min(MAX_SPEED_KMH, speed));

  // Declared pauses not over yet (a running one) still delay the arrival.
  const pauseLeftMs = remainingPauseMs(pauses, lastTimestamp);
  const estimatedArrival = speed
    ? new Date(lastTimestamp + (remainingKm / speed) * 3600000 + pauseLeftMs) : null;
  const delayMinutes = estimatedArrival && plannedArrival
    ? Math.round((estimatedArrival.getTime() - new Date(plannedArrival).getTime()) / 60000)
    : null;

  return {
    remainingKm: Math.max(0, remainingKm), estimatedArrival, delayMinutes, approximate,
  };
};

export default computeTripProgress;
